import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import im from 'immer'
import { v4 as uuid } from 'uuid';

import { Dispatch } from 'redux'
import { FolderItemORM, CustomDeckORM } from 'src/types/types'

import { GenericToast, ToastOrientations, useToast } from '@alucio/lux-ui'
import { ToastActions } from '@alucio/lux-ui/lib/components/Toast/useToast'
import { folderActions } from 'src/state/redux/slice/folder'
import DNADocumentAddToFolder from '../Modal/DNADocumentAddToFolder'
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal'
import { presentationBuilderActions } from 'src/state/redux/slice/PresentationBuilder/PresentationBuilder'
import cloneDeep from 'lodash/cloneDeep'
import { contentPreviewModalActions } from 'src/state/redux/slice/contentPreviewModal'
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings'

import API, { graphqlOperation } from '@aws-amplify/api';
import {
  downloadCustomDeckAsPdfFile,
} from '@alucio/aws-beacon-amplify/src/graphql/mutations'
import { download } from 'src/screens/Documents/Export'

export type CustomDeckContextOptions = {
  [key in keyof typeof CustomDeckContextActions]: {
    icon: string,
    title: CustomDeckContextActions,
    onPress: (
      d: CustomDeckORM,
      dispatch: Dispatch,
      toast: ToastActions,
      folderItemORM?: FolderItemORM,
      isTablet?: boolean,
      context?: string,
    ) => () => void
  }
}

export type BindCustomDeckContextActions = {
  [key in keyof typeof CustomDeckContextActions]: (d: CustomDeckORM, f?: FolderItemORM, c?: string) => () => void
}

export enum CustomDeckContextActions {
  addToFolder = 'Add to folder',
  downloadAsPDF = 'Download as PDF',
  duplicate = 'Duplicate',
  edit = 'Edit',
  delete = 'Delete',
  present = 'Present'
}

export type CustomDeckActions = (keyof typeof CustomDeckContextActions)

export const useDNACustomDeckActions = (): BindCustomDeckContextActions => {
  const dispatch = useDispatch()
  const toast = useToast()
  const { deviceMode } = useAppSettings()
  const bound = useMemo(() => im(customDeckContextOptions, draft => {
    for (const action in customDeckContextOptions) {
      const actionTyped = action as keyof typeof CustomDeckContextActions
      draft[action] = (
        customDeckORM: CustomDeckORM,
        folderItemORM: FolderItemORM,
        context?: string, // indicates from where this action was done for tracking purposes
      ) => customDeckContextOptions[actionTyped].onPress(
        customDeckORM,
        dispatch,
        toast,
        folderItemORM,
        deviceMode === DeviceMode.tablet,
        context,
      )
    }
  }) as unknown as BindCustomDeckContextActions,
  [dispatch, toast],
  )

  return bound
}

export const customDeckContextOptions: CustomDeckContextOptions = {
  addToFolder: {
    icon: 'folder-plus',
    title: CustomDeckContextActions.addToFolder,
    onPress: (_, dispatch, toast, folderItemORM) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (props) => (
          <DNADocumentAddToFolder
            {...props}
            itemORM={folderItemORM!}
            toast={toast}
          />
        ),
      }),
      )
    },
  },
  edit: {
    icon: 'pencil',
    title: CustomDeckContextActions.edit,
    onPress: (currentCustomDeck, dispatch, _, folderItemORM, __, context) => () => {
      dispatch(presentationBuilderActions.openPresentationBuilderEdit({
        customDeckId: currentCustomDeck.model.id,
        folderItemORM,
        targetFolder: folderItemORM?.relations.parent!,
        context,
      }));
    },
  },
  delete: {
    icon: 'trash-can-outline',
    title: CustomDeckContextActions.delete,
    onPress: (_, dispatch, toast, folderItemORM) => () => {
      const targetFolderId = folderItemORM?.relations.parent?.model.id

      if (!targetFolderId) {
        console.error('Could not determine the current folder path')
        return;
      }

      // NOTE: currently we are not removing the custom deck from the DB
      // we are only removing the reference in the folderItem
      if (folderItemORM) {
        analytics?.track('CUSTOM_DELETE', {
          action: 'DELETE',
          category: 'CUSTOM',
          customDeckId: folderItemORM.model.id,
        })
        dispatch(folderActions.removeItem(folderItemORM))
        toast.add(
          <GenericToast
            title="Personalized File deleted."
            status="success"
          />,
          ToastOrientations.TOP_RIGHT,
        )
      } else {
        console.error('Could not determine the current personalized file to delete')
      }
    },
  },
  duplicate: {
    icon: 'content-copy',
    title: CustomDeckContextActions.duplicate,
    onPress: (_, dispatch, __, folderItemORM) => () => {
      const targetFolder = folderItemORM?.relations.parent

      if (!targetFolder) {
        console.error('Could not determine the current folder path')
        return;
      }

      if (!folderItemORM) {
        console.error('Could not determine the current custom deck')
        return;
      }

      dispatch(
        folderActions.duplicateItem(
          'Untitled Presentation',
          targetFolder,
          {
            ...cloneDeep(folderItemORM.model),
            id: uuid(),
          },
        ),
      )
    },
  },
  present: {
    icon: 'play',
    title: CustomDeckContextActions.present,
    onPress: (customDeckORM, dispatch, __, folderItemORM) => () => {
      for (const group of customDeckORM.meta.groupsORM) {
        if (group.model.visible && group.pages[0]) {
          dispatch(
            contentPreviewModalActions.setModalVisibility({
              documentVersionId: group.pages[0].documentVersionORM.model.id,
              folderItemORM: folderItemORM,
              isFullWindow: true,
              fromEllipsis: true,
              isOpen: true,
            },
            ))
          break;
        }
      }
    },
  },
  downloadAsPDF: {
    icon: 'file-pdf-box',
    title: CustomDeckContextActions.downloadAsPDF,
    onPress: (customDeck, _, toast) => async () => {
      toast.add(
        <GenericToast
          title="Generating PDF download..."
          status="information"
        />,
        ToastOrientations.TOP_RIGHT,
      )
      try {
        const { data } = await API.graphql(
          graphqlOperation(downloadCustomDeckAsPdfFile, {
            customDeckId: customDeck.model.id,
          }),
        ) as { data: { downloadCustomDeckAsPdfFile: string } };

        download(data.downloadCustomDeckAsPdfFile, `${customDeck.meta.title?.replace('.pptx', '')}.pdf`)
      } catch (e) {
        console.warn(`Error downloading the pdf file for this document id ${customDeck.model.id}`, e)
      }
    },
  },
}
